import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { HeroBackground } from '@/components/HeroBackground';
import { AboutDialog } from '@/components/AboutDialog';
import { useAuth } from './AuthContext';
import casperLogo from '@/assets/casperLogoIcon.png';
import cilLogo from '@/assets/CILLogo.png';
import ciscoLogoWhite from '@/assets/CiscoLogoWhite.svg';

const AUTH_ERROR_COPY: Record<string, { title: string; description?: string }> = {
  denied:            { title: 'Sign-in was cancelled.' },
  domain:            { title: 'That account isn’t allowed here.', description: 'Only Cisco Webex identities can sign in.' },
  disabled:          { title: 'Your account has been disabled.', description: 'Ask an administrator to re-enable it.' },
  state:             { title: 'Sign-in link expired.', description: 'Please try again.' },
  exchange:          { title: 'We couldn’t reach Webex.', description: 'Please try again in a moment.' },
  invalid_response:  { title: 'Sign-in failed.', description: 'Please try again.' },
};

export function LoginPage() {
  const { signIn } = useAuth();
  const [busy, setBusy] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error');
    if (!err) return;
    const copy = AUTH_ERROR_COPY[err] ?? { title: 'Sign-in failed.', description: err };
    toast.error(copy.title, copy.description ? { description: copy.description } : undefined);
    params.delete('auth_error');
    const qs = params.toString();
    const next = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState({}, '', next);
  }, []);

  async function onSignIn() {
    setBusy(true);
    try {
      await signIn();
    } catch (err) {
      setBusy(false);
      toast.error('Could not start sign-in', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      <HeroBackground />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        <div className="w-full rounded-2xl border border-white/10 bg-white/[0.06] p-8 text-center shadow-2xl backdrop-blur-xl">
          <img src={ciscoLogoWhite} alt="Cisco" className="mx-auto h-12 w-auto" />
          <img
            src={cilLogo}
            alt="Cisco Innovation Labs"
            className="mx-auto mt-3 h-10 w-auto rounded-md bg-white px-3 py-1.5"
          />
          <img src={casperLogo} alt="cAspeR" className="mx-auto mt-6 h-16 w-auto" />
          <div className="mt-6 space-y-1.5">
            <h1 className="text-3xl font-semibold tracking-tight text-white">cAspeR</h1>
            <p className="text-sm text-white/60">
              Synchronised AR demos, driven live from one console.
            </p>
          </div>

          <Button
            type="button"
            className="mt-8 w-full"
            size="lg"
            disabled={busy}
            onClick={onSignIn}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in with Webex'}
          </Button>
          <p className="mt-3 text-center text-xs text-white/50">
            Cisco Webex identities only. Access is granted after your first sign-in.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setAboutOpen(true)}
          className="mt-6 text-xs font-medium text-white/50 transition-colors hover:text-white"
        >
          About cAspeR
        </button>
      </div>

      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}
